import React, { useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Checkbox,
  Select,
  Button,
  Upload,
  Alert,
  DatePicker,
  Modal,
  message,
} from 'antd';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';
import type { RcFile } from 'antd/es/upload/interface';
import { useTranslate, useApiUrl, useSelect, useNavigation } from '@pankod/refine-core';
import axios from 'axios';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import UserHandler from 'util/UserHandler';
import { ConveyanceStatus } from 'types/ownership/conveyanceStatus';
import { ConveyanceType } from 'types/ownership/conveyanceType';
import { ConveyanceAttachmentType } from 'types/ownership/conveyanceAttachmentType';
import { AttachmentValidationStatus } from 'types/ownership/attachmentValidationStatus';
import { CustomUploadFile } from 'types/ownership/customUploadFile';
import { Ownership, User } from 'interfaces';
import { EnumToArray } from 'util/parseData';

// Extend dayjs with the plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const ConveyanceCreate: React.FC = () => {
  const [formConveyance] = Form.useForm();
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const authUser = UserHandler.getInstance().user;
  const { TextArea } = Input;
  const { list } = useNavigation();

  const [newConveyanceVisible, setNewConveyanceVisible] = useState(true);
  const [newConveyanceError, setNewConveyanceError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [conveyanceStatus, setConveyanceStatus] = useState<ConveyanceStatus | undefined>();
  const [conveyanceAttachments, setConveyanceAttachments] = useState<CustomUploadFile[]>([]);

  const beforeUpload = (file: RcFile) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    const isValidType = allowedTypes.includes(file.type);

    if (!isValidType) {
      message.error(t('messages.unsupported_format', { ns: 'conveyances' }));
      return Upload.LIST_IGNORE;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error(t('messages.file_size_exceeded', { ns: 'conveyances' }));
    }

    return isValidType && isLt5M;
  };

  const conveyanceStatusOptions = EnumToArray(ConveyanceStatus).map((op) => ({
    value: op.value,
    label: t(`status.${op.label}`, { ns: 'conveyances' }),
  }));
  const conveyanceTypeOptions = EnumToArray(ConveyanceType).map((op) => ({
    value: op.value,
    label: t(`types.${op.label}`, { ns: 'conveyances' }),
  }));
  const attachmentTypeOptions = EnumToArray(ConveyanceAttachmentType).map((op) => ({
    value: op.value,
    label: t(`types.${op.label}`, { ns: 'attachments' }),
  }));
  const attachmentStatusOptions = EnumToArray(AttachmentValidationStatus).map((op) => ({
    value: op.value,
    label: t(`status.${op.label}`, { ns: 'attachments' }),
  }));

  const { options: ownershipOptions } = useSelect<Ownership>({
    resource: 'ownership',
    optionLabel: 'name',
    optionValue: 'id',
  });

  const { options: rawUserOptions, onSearch: onSearchUser } = useSelect<User>({
    resource: 'user',
    optionLabel: 'full_name',
    optionValue: 'id',
  });

  const userOptions = rawUserOptions.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  const newConveyanceHandleCancel = () => {
    formConveyance.resetFields();
    setConveyanceAttachments([]);
    setNewConveyanceVisible(false);
    list('conveyance');
  };

  const conveyanceHandleChange =
    (index: number) =>
    ({ fileList: newFileList }: UploadChangeParam<UploadFile>) => {
      const updatedFileList: CustomUploadFile[] = newFileList.map((file) => {
        if (file.response && file.response.filePath) {
          return {
            ...file,
            url: process.env.REACT_APP_SERVER + file.response.filePath,
            path: file.response.filePath,
            status: 'done' as const,
            conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
            custom_type: null,
          } as CustomUploadFile;
        }
        return {
          ...file,
          conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
          custom_type: null,
        } as CustomUploadFile;
      });

      const currentAttachments = formConveyance.getFieldValue('attachments') || [];
      currentAttachments[index] = {
        ...currentAttachments[index],
        path: updatedFileList[0]?.path || '',
        conveyance_type: updatedFileList[0]?.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
        custom_type: updatedFileList[0]?.custom_type || null,
        details: updatedFileList[0]?.details,
        document_number: updatedFileList[0]?.document_number,
        issue_date: updatedFileList[0]?.issue_date,
        expiration_date: updatedFileList[0]?.expiration_date,
        validation_status: updatedFileList[0]?.validation_status || AttachmentValidationStatus.waiting,
        observations: updatedFileList[0]?.observations,
      };
      formConveyance.setFieldsValue({ attachments: currentAttachments });
      setConveyanceAttachments((prev) => {
        const newAttachments = [...prev];
        newAttachments[index] = updatedFileList[0] || {
          uid: `attach-${index}-${Date.now()}`,
          name: '',
          status: 'error' as const,
          conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
          custom_type: null,
        };
        return newAttachments;
      });
    };

  const handleRemoveAttachment = (name: number, index: number, remove: (index: number) => void) => {
    const file = conveyanceAttachments[index];

    setConveyanceAttachments((prev) => {
      const newAttachments = [...prev];
      newAttachments.splice(index, 1);
      return newAttachments;
    });
    remove(name);
    message.success(t('messages.attachment_deleted_locally', { ns: 'conveyances' }));

    if (file?.path) {
      handleRemove(file, index).catch((err) => {
        console.error('Erreur lors de la suppression serveur:', err);
        message.error(t('messages.attachment_delete_failed', { ns: 'conveyances' }));
      });
    }
  };

  const handleRemove = async (file: UploadFile | CustomUploadFile, index?: number): Promise<void> => {
    try {
      const customFile: CustomUploadFile = {
        ...file,
        path: file.response?.filePath || (file as CustomUploadFile).path || '',
        conveyance_type: (file as CustomUploadFile).conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
      };

      const ownershipId = formConveyance.getFieldValue('ownership_id');

      if (!customFile.path || !ownershipId) {
        if (index !== undefined) {
          setConveyanceAttachments((prev: CustomUploadFile[]) => {
            const newAttachments = [...prev];
            newAttachments.splice(index, 1);
            return newAttachments;
          });
          const currentAttachments = formConveyance.getFieldValue('attachments') || [];
          currentAttachments.splice(index, 1);
          formConveyance.setFieldsValue({ attachments: currentAttachments });
        }
        return;
      }

      await axios.delete(`${apiUrl}/attachments/by-path`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${UserHandler.getInstance().token}`,
        },
        data: {
          path: customFile.path,
          ownership_id: ownershipId,
          conveyance_id: null,
        },
      });

      if (index !== undefined) {
        setConveyanceAttachments((prev: CustomUploadFile[]) => {
          const newAttachments = [...prev];
          newAttachments.splice(index, 1);
          return newAttachments;
        });
        const currentAttachments = formConveyance.getFieldValue('attachments') || [];
        currentAttachments.splice(index, 1);
        formConveyance.setFieldsValue({ attachments: currentAttachments });
      }
      message.success(t('messages.attachment_deleted_success', { ns: 'conveyances' }));
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      message.error(t('messages.attachment_delete_failed', { ns: 'conveyances' }));
      throw err;
    }
  };

  const newConveyanceHandleOk = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setNewConveyanceError(null);
    try {
      await formConveyance.validateFields();
      const formData = { ...formConveyance.getFieldsValue() };
      formData.created_by = authUser?.id;
      formData.updated_by = authUser?.id;
      formData.start = formData.start ? formData.start.toISOString() : null;
      formData.end = formData.end ? formData.end.toISOString() : null;
      formData.amount = formData.amount ? parseFloat(formData.amount) : 0;
      formData.interval = formData.interval ? parseInt(formData.interval, 10) : 0;
      formData.attachments = (formData.attachments || []).map((a: any) => ({
        ...a,
        path: a.path || a.response?.filePath || null,
        issue_date: a.issue_date
          ? dayjs.isDayjs(a.issue_date)
            ? a.issue_date.toISOString()
            : typeof a.issue_date === 'string'
            ? new Date(a.issue_date).toISOString()
            : null
          : null,
        expiration_date: a.expiration_date
          ? dayjs.isDayjs(a.expiration_date)
            ? a.expiration_date.toISOString()
            : typeof a.expiration_date === 'string'
            ? new Date(a.expiration_date).toISOString()
            : null
          : null,
        conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
        custom_type: a.custom_type || null,
        validation_status: a.validation_status || AttachmentValidationStatus.waiting,
        observations: a.observations || null,
        document_number: a.document_number || null,
        details: a.details || null,
      }));

      const response = await axios.post(`${apiUrl}/conveyance`, formData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${UserHandler.getInstance().token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        message.success(t('messages.conveyance_created_success', { ns: 'conveyances' }));
        formConveyance.resetFields();
        setConveyanceAttachments([]);
        setNewConveyanceVisible(false);
        list('conveyance');
      }
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', {
        error,
        response: error.response?.data,
        status: error.response?.status,
        fullError: error,
      });
      let errorMessage = t('fields.conveyance_error', { ns: 'conveyances' });
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setNewConveyanceError(errorMessage);
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('fields.new_conveyance', { ns: 'conveyances' })}
      visible={newConveyanceVisible}
      onOk={newConveyanceHandleOk}
      onCancel={newConveyanceHandleCancel}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText={t('buttons.save', { ns: 'conveyances' })}
      width={1000}
    >
      {newConveyanceError && (
        <Alert
          message={newConveyanceError}
          type="error"
          showIcon
          closable
          onClose={() => setNewConveyanceError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={formConveyance} name="new_conveyance_form" layout="vertical">
        <Row>
          <Col className="gutter-row" span={24} style={{ marginBottom: '8px' }}>
            <Form.Item name="auto_renew" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.auto_renew', { ns: 'conveyances' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.ownership_id', { ns: 'conveyances' })}
              name="ownership_id"
              rules={[{ required: true, message: t('messages.select_property', { ns: 'conveyances' }) }]}
            >
              <Select options={ownershipOptions} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.title', { ns: 'conveyances' })}
              name="title"
              rules={[{ required: true, message: t('messages.enter_title', { ns: 'conveyances' }) }]}
            >
              <Input maxLength={255} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.type', { ns: 'conveyances' })}
              name="type"
              rules={[{ required: true, message: t('messages.select_type', { ns: 'conveyances' }) }]}
            >
              <Select options={conveyanceTypeOptions} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.grant_to', { ns: 'conveyances' })}
              name="grant_to"
              rules={[{ required: true, message: t('messages.select_contractor', { ns: 'conveyances' }) }]}
            >
              <Select
                options={userOptions}
                onSearch={onSearchUser}
                showSearch
                filterOption={false}
                fieldNames={{ label: 'label', value: 'value' }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.status', { ns: 'conveyances' })}
              name="status"
              rules={[{ required: true, message: t('messages.select_status', { ns: 'conveyances' }) }]}
            >
              <Select
                options={conveyanceStatusOptions}
                onChange={(value) => setConveyanceStatus(value)}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.amount', { ns: 'conveyances' })}
              name="amount"
              rules={[{ required: true, message: t('messages.enter_amount', { ns: 'conveyances' }) }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.interval', { ns: 'conveyances' })}
              name="interval"
              rules={[{ required: true, message: t('messages.enter_interval', { ns: 'conveyances' }) }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.start', { ns: 'conveyances' })}
              name="start"
              getValueProps={(value: any) => ({
                value: value ? dayjs(value) : null,
              })}
              rules={[
                { required: true, message: t('messages.enter_start_date', { ns: 'conveyances' }) },
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const endDate = getFieldValue('end');
                    if (!value || !endDate) {
                      return Promise.resolve();
                    }
                    if (dayjs(value).isSameOrAfter(dayjs(endDate))) {
                      return Promise.reject(
                        new Error(t('messages.start_date_before_end', { ns: 'conveyances' })),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.end', { ns: 'conveyances' })}
              name="end"
              getValueProps={(value: any) => ({
                value: value ? dayjs(value) : null,
              })}
              rules={[
                { required: true, message: t('messages.enter_end_date', { ns: 'conveyances' }) },
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const startDate = getFieldValue('start');
                    if (!value || !startDate) {
                      return Promise.resolve();
                    }
                    if (dayjs(value).isSameOrBefore(dayjs(startDate))) {
                      return Promise.reject(
                        new Error(t('messages.end_date_after_start', { ns: 'conveyances' })),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.description', { ns: 'conveyances' })}
              name="description"
            >
              <TextArea maxLength={255} rows={3} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={24}>
            <Form.Item label={t('fields.note', { ns: 'conveyances' })} name="note">
              <TextArea />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={24}>
            <Form.List name="attachments">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Row
                      key={key}
                      gutter={[16, 16]}
                      style={{
                        marginBottom: '16px',
                        borderBottom: '1px solid #f0f0f0',
                        paddingBottom: '16px',
                      }}
                    >
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.attachment_file', { ns: 'attachments' })}
                          name={[name, 'path']}
                        >
                          <Upload
                            action={`${apiUrl}/media/uploadending/${formConveyance.getFieldValue(
                              'ownership_id',
                            )}/conveyance`}
                            data={{
                              table: `ownerships/${formConveyance.getFieldValue('ownership_id')}/conveyance`,
                            }}
                            listType="text"
                            accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/gif,image/webp"
                            onChange={conveyanceHandleChange(index)}
                            onRemove={() => {
                              handleRemoveAttachment(name, index, remove);
                              return false;
                            }}
                            fileList={conveyanceAttachments[index] ? [conveyanceAttachments[index]] : []}
                            maxCount={1}
                            beforeUpload={beforeUpload}
                          >
                            {(!conveyanceAttachments[index] ||
                              conveyanceAttachments[index].status !== 'done') && (
                              <Button style={{ borderRadius: '6px', padding: '4px 15px' }}>
                                {t('buttons.add_attachment', { ns: 'attachments' })}
                              </Button>
                            )}
                          </Upload>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.type', { ns: 'attachments' })}
                          name={[name, 'conveyance_type']}
                          rules={[
                            {
                              required: true,
                              message: t('messages.select_attachment_type', { ns: 'attachments' }),
                            },
                          ]}
                        >
                          <Select
                            options={attachmentTypeOptions}
                            onChange={(value) => {
                              const currentAttachments =
                                formConveyance.getFieldValue('attachments') || [];
                              currentAttachments[index] = {
                                ...currentAttachments[index],
                                conveyance_type: value,
                                custom_type:
                                  value !== ConveyanceAttachmentType.Autre
                                    ? null
                                    : currentAttachments[index]?.custom_type || null,
                              };
                              formConveyance.setFieldsValue({ attachments: currentAttachments });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      {(() => {
                        const attachmentType = formConveyance.getFieldValue([
                          'attachments',
                          name,
                          'conveyance_type',
                        ]);
                        const isAutre = attachmentType === ConveyanceAttachmentType.Autre;
                        const customTypeValue = formConveyance.getFieldValue([
                          'attachments',
                          name,
                          'custom_type',
                        ]);
                        return isAutre ? (
                          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                            <Form.Item
                              {...restField}
                              label={t('fields.custom_type', { ns: 'attachments' })}
                              name={[name, 'custom_type']}
                              rules={[
                                {
                                  required: true,
                                  message: t('messages.enter_custom_type', { ns: 'attachments' }),
                                },
                              ]}
                              initialValue={customTypeValue}
                            >
                              <Input maxLength={255} />
                            </Form.Item>
                          </Col>
                        ) : null;
                      })()}
                      <Col xs={24} sm={24} md={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.details', { ns: 'attachments' })}
                          name={[name, 'details']}
                        >
                          <TextArea maxLength={255} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'document_number']}
                          label={t('fields.document_number', { ns: 'attachments' })}
                        >
                          <Input maxLength={255} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.issue_date', { ns: 'attachments' })}
                          name={[name, 'issue_date']}
                          getValueProps={(value: any) => ({ value: value ? dayjs(value) : null })}
                        >
                          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.expiration_date', { ns: 'attachments' })}
                          name={[name, 'expiration_date']}
                          getValueProps={(value: any) => ({ value: value ? dayjs(value) : null })}
                        >
                          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24}>
                        <Form.Item
                          {...restField}
                          label={t('fields.observations', { ns: 'attachments' })}
                          name={[name, 'observations']}
                        >
                          <TextArea maxLength={500} />
                        </Form.Item>
                      </Col>
                      <Col span={24} style={{ textAlign: 'right' }}>
                        <Form.Item>
                          <Button
                            type="primary"
                            danger
                            onClick={() => handleRemoveAttachment(name, index, remove)}
                          >
                            {t('buttons.delete_attachment', { ns: 'attachments' })}
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => {
                        add({
                          conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
                          validation_status: AttachmentValidationStatus.waiting,
                          custom_type: null,
                        });
                        setConveyanceAttachments((prev) => [
                          ...prev,
                          {
                            uid: `attach-${fields.length + 1}-${Date.now()}`,
                            name: '',
                            status: 'error' as const,
                            conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
                            custom_type: null,
                          },
                        ]);
                      }}
                      block
                      icon={<i className="anticon anticon-plus" />}
                      disabled={fields.length >= 5}
                    >
                      {t('buttons.add_attachment', { ns: 'attachments' })}
                    </Button>
                  </Form.Item>
                </div>
              )}
            </Form.List>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};